import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import Section from './components/Section';

// Images — newspapers & journalism
import heroImg from 'src/assets/images/landing/blog/from-5ws-to-front-page-journalism-across-grade-levels.jpg';
import nycStudentsImg from 'src/assets/images/landing/blog/nyc-students-reporting-from-brooklyn-borough-hall.jpeg';
import crossCurricularImg from 'src/assets/images/landing/blog/cross-curricular-journalism-beyond-ela.jpg';
import principalGuideImg from 'src/assets/images/landing/blog/empowering-students-in-underserved-communities-through-innovative-writing-technology.png';
import scienceOfReadingImg from 'src/assets/images/landing/blog/student-journalism-and-the-science-of-reading.jpg';
import selImg from 'src/assets/images/landing/blog/student-voice-and-sel-through-journalism.jpg';
import step1Gif from 'src/assets/images/landing/step-1-inspire.gif';
import step2Gif from 'src/assets/images/landing/step-2-empower.gif';
import step3Gif from 'src/assets/images/landing/step-3-support.gif';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '3px 3px 0px #1e1e1e';

const STORY_TEMPLATES = [
  { emoji: '📰', label: 'News Story' },
  { emoji: '✨', label: 'Feature Story' },
  { emoji: '🎤', label: 'Interview' },
  { emoji: '📚', label: 'Book Review' },
  { emoji: '🎬', label: 'Movie Review' },
  { emoji: '🎮', label: 'Game Review' },
  { emoji: '🍕', label: 'Restaurant Review' },
  { emoji: '🧑‍🍳', label: 'Recipe' },
  { emoji: '💬', label: 'Opinion' },
  { emoji: '📷', label: 'Photo Essay' },
];

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: '#2C2C2C',
        color: '#FFFFFF',
        fontFamily: 'Montserrat, Inter, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        textTransform: 'none',
        borderRadius: '6px',
        border: '2px solid #1e1e1e',
        boxShadow: SHADOW,
        px: 4,
        py: 1.5,
        '&:hover': {
          backgroundColor: '#1e1e1e',
          boxShadow: '2px 2px 0px #1e1e1e',
          transform: 'translate(2px, 2px)',
        },
        transition: 'all 100ms ease-in-out',
      }}
    >
      {children}
    </MuiButton>
  );
}

function YellowButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: '#FFE082',
        color: '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif',
        fontWeight: 700,
        fontSize: '1rem',
        textTransform: 'none',
        borderRadius: '6px',
        border: '2px solid #1e1e1e',
        boxShadow: SHADOW,
        px: 5,
        py: 1.5,
        '&:hover': {
          backgroundColor: '#FFD54F',
          boxShadow: '2px 2px 0px #1e1e1e',
          transform: 'translate(2px, 2px)',
        },
        transition: 'all 100ms ease-in-out',
      }}
    >
      {children}
    </MuiButton>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 6, md: 8 },
            alignItems: 'center',
          }}
        >
          {/* Left: copy */}
          <Box>
            <Typography
              sx={{
                display: 'inline-block',
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#2C2C2C',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                backgroundColor: '#FFE082',
                border: '2px solid #1e1e1e',
                boxShadow: '2px 2px 0px #1e1e1e',
                px: 2.5,
                py: 0.75,
                borderRadius: '4px',
                mb: 3,
              }}
            >
              For K-5 Classrooms
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Montserrat, Inter, sans-serif',
                fontSize: { xs: '2.25rem', md: '3.25rem' },
                fontWeight: 800,
                lineHeight: 1.12,
                mb: 3,
                color: '#2C2C2C',
                letterSpacing: '-0.02em',
              }}
            >
              Your Classroom&apos;s{' '}
              <Box
                component="span"
                sx={{
                  textDecoration: 'underline',
                  textDecorationColor: '#FFB300',
                  textUnderlineOffset: '6px',
                  textDecorationThickness: '4px',
                }}
              >
                Own Newspaper
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato, Inter, sans-serif',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.7,
                color: '#555',
                mb: 4,
                maxWidth: 500,
              }}
            >
              Students pick stories, write with built-in scaffolds, and publish a real newspaper — all inside one tool their teacher controls.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <PrimaryButton onClick={() => navigate('/register')}>
                Start Your Newspaper — Free
              </PrimaryButton>
            </Box>
          </Box>

          {/* Right: newspaper hero image */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              component="img"
              src={heroImg}
              alt="Elementary students working on a classroom newspaper"
              sx={{
                width: '100%',
                borderRadius: '8px',
                border: '2px solid #1e1e1e',
                boxShadow: '6px 6px 0px #1e1e1e',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── How It Works (3 steps, newspaper-focused) ─────────────────────────────────
function HowItWorks() {
  const STEPS = [
    {
      step: '1',
      title: 'Create Your Newspaper',
      desc: 'Name it, link your classrooms, and you\'re set. Takes about 30 seconds.',
      accent: '#FFE082',
      gif: step1Gif,
    },
    {
      step: '2',
      title: 'Open an Edition & Assign Stories',
      desc: 'Pick a theme and assign story types to your students. Each writer knows exactly what they\'re working on.',
      accent: '#A5D6A7',
      gif: step2Gif,
    },
    {
      step: '3',
      title: 'Students Write & Publish',
      desc: 'Students write with built-in scaffolds and submit their stories. You review, then publish the edition — and share it with families and the whole school community.',
      accent: '#90CAF9',
      gif: step3Gif,
    },
  ];

  return (
    <Section background="#F7F7F7" id="how-it-works">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          Three Steps to a Published Newspaper
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#555',
            maxWidth: 550,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          No design skills needed. No printing headaches. Just student writing.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 6, md: 8 } }}>
        {STEPS.map((step, index) => (
          <Box
            key={step.title}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center',
              direction: { md: index % 2 === 1 ? 'rtl' : 'ltr' },
            }}
          >
            <Box sx={{ direction: 'ltr', order: { xs: 0, md: index % 2 === 1 ? 1 : 0 } }}>
              <Box
                component="img"
                src={step.gif}
                alt={step.title}
                sx={{ width: '100%', display: 'block', borderRadius: '4px' }}
              />
            </Box>
            <Box sx={{ direction: 'ltr' }}>
              <Box
                sx={{
                  display: 'inline-block',
                  backgroundColor: step.accent,
                  border: '2px solid #1e1e1e',
                  borderRadius: '4px',
                  px: 1.5,
                  py: 0.5,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    color: '#2C2C2C',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Step {step.step}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  color: '#2C2C2C',
                  lineHeight: 1.15,
                  mb: 1.5,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  color: '#555',
                  lineHeight: 1.8,
                }}
              >
                {step.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── Story Templates ───────────────────────────────────────────────────────────
function StoryTemplates() {
  return (
    <Section background="#FFFFFF">
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          10 Story Types. Built-in Scaffolds for Each.
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#555',
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          Students choose their story type and get structure, sentence starters, and signal words matched to that genre — so every writer has a way in.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(5, 1fr)' },
          gap: 2,
        }}
      >
        {STORY_TEMPLATES.map((t) => (
          <Box
            key={t.label}
            sx={{
              p: 2.5,
              borderRadius: '6px',
              border: '2px solid #1e1e1e',
              boxShadow: SHADOW_SM,
              textAlign: 'center',
              backgroundColor: '#FFFFFF',
              transition: 'all 100ms ease-in-out',
              cursor: 'default',
              '&:hover': {
                boxShadow: '1px 1px 0px #1e1e1e',
                transform: 'translate(2px, 2px)',
              },
            }}
          >
            <Typography sx={{ fontSize: '2rem', mb: 0.5 }}>{t.emoji}</Typography>
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '0.8rem',
                color: '#2C2C2C',
              }}
            >
              {t.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── Why Newspapers (evidence section with images) ─────────────────────────────
function WhyNewspapers() {
  const REASONS = [
    {
      img: scienceOfReadingImg,
      title: 'Grounded in the Science of Reading',
      desc: 'Journalism connects reading comprehension and writing fluency — exactly what the research says K-5 students need.',
    },
    {
      img: selImg,
      title: 'Student Voice & SEL',
      desc: 'When students write for a real audience, they build confidence, empathy, and a sense of belonging in their school community.',
    },
    {
      img: crossCurricularImg,
      title: 'Cross-Curricular, Not Just ELA',
      desc: 'Science fair reports. Community profiles. Math puzzles. A newspaper is a home for writing across every subject.',
    },
  ];

  return (
    <Section background="#F7F7F7">
      <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            mb: 2,
          }}
        >
          Why a Classroom Newspaper?
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#555',
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.7,
          }}
        >
          It&apos;s not an extra — it&apos;s the vehicle for everything you&apos;re already teaching.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        {REASONS.map((r) => (
          <Box
            key={r.title}
            sx={{
              borderRadius: '6px',
              border: '2px solid #1e1e1e',
              boxShadow: SHADOW,
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              transition: 'all 100ms ease-in-out',
              '&:hover': {
                boxShadow: '2px 2px 0px #1e1e1e',
                transform: 'translate(2px, 2px)',
              },
            }}
          >
            <Box
              component="img"
              src={r.img}
              alt={r.title}
              sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
            />
            <Box sx={{ p: 3 }}>
              <Typography
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#2C2C2C',
                  mb: 1,
                }}
              >
                {r.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.9rem',
                  color: '#555',
                  lineHeight: 1.7,
                }}
              >
                {r.desc}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── Social proof with real images ─────────────────────────────────────────────
function SocialProof() {
  return (
    <Section background="#FFFFFF">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Box
            component="img"
            src={nycStudentsImg}
            alt="NYC students reporting from Brooklyn Borough Hall"
            sx={{
              width: '100%',
              borderRadius: '8px',
              border: '2px solid #1e1e1e',
              boxShadow: '6px 6px 0px #1e1e1e',
            }}
          />
        </Box>
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 800,
              color: '#2C2C2C',
              mb: 2,
            }}
          >
            Real Students. Real Reporting.
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lato, Inter, sans-serif',
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: '#555',
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            From Brooklyn Borough Hall to school cafeterias, Pressto newspapers give students a reason to write — and a real audience to write for. When their words get published, writing stops being an assignment and becomes something they&apos;re proud of.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 2,
            }}
          >
            {[
              { number: '100+', label: 'School Districts' },
              { number: '10', label: 'Story Types' },
              { number: 'K-5', label: 'Grade Levels' },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  textAlign: 'center',
                  p: 2,
                  borderRadius: '6px',
                  border: '2px solid #1e1e1e',
                  boxShadow: '2px 2px 0px #1e1e1e',
                  backgroundColor: '#faf7f0',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    color: '#2C2C2C',
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.7rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Section>
  );
}

// ─── Privacy (compact) ─────────────────────────────────────────────────────────
function PrivacyCompact() {
  return (
    <Section background="#2C2C2C" dark>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 3,
          textAlign: 'center',
        }}
      >
        {[
          { icon: '🛡', title: 'FERPA & COPPA', desc: 'Compliant by design, not just policy' },
          { icon: '🔒', title: 'No Student PII to AI', desc: 'Personal info stripped before any AI interaction' },
          { icon: '👩‍🏫', title: 'Teacher Controls', desc: 'Enable or disable AI features per classroom' },
          { icon: '🏫', title: 'District Admin Tools', desc: 'Role-based access, encryption, full data control' },
        ].map((p) => (
          <Box key={p.title} sx={{ p: 3 }}>
            <Typography sx={{ fontSize: '2rem', mb: 1 }}>{p.icon}</Typography>
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#FFFFFF',
                mb: 0.5,
              }}
            >
              {p.title}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.6,
              }}
            >
              {p.desc}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

// ─── For Principals (bonus audience) ───────────────────────────────────────────
function ForPrincipals() {
  return (
    <Section background="#FFFFFF">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              display: 'inline-block',
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#2C2C2C',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              backgroundColor: '#A5D6A7',
              border: '2px solid #1e1e1e',
              boxShadow: '2px 2px 0px #1e1e1e',
              px: 2.5,
              py: 0.75,
              borderRadius: '4px',
              mb: 3,
            }}
          >
            For Principals & Curriculum Leads
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 800,
              color: '#2C2C2C',
              mb: 2,
            }}
          >
            A Newspaper for Every Classroom — Not Just the Gifted Program
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lato, Inter, sans-serif',
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: '#555',
              lineHeight: 1.8,
              mb: 3,
            }}
          >
            Pressto&apos;s scaffolded approach means every student can participate — ELLs, students with IEPs, and reluctant writers included. The newspaper becomes a schoolwide initiative, not an afterschool club.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              'Works across grade levels K-5',
              'Built-in writing scaffolds for every story type',
              'Tie editions to curriculum themes across subjects',
              'Teacher reviews before anything is published',
              'Share published editions with families',
            ].map((point) => (
              <Box key={point} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Typography sx={{ color: '#4CAF50', fontWeight: 700, fontSize: '1rem', lineHeight: 1.6 }}>
                  &#10003;
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    color: '#444',
                    lineHeight: 1.6,
                  }}
                >
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <Box
            component="img"
            src={principalGuideImg}
            alt="Principal guide to school newspapers"
            sx={{
              width: '100%',
              borderRadius: '8px',
              border: '2px solid #1e1e1e',
              boxShadow: '6px 6px 0px #1e1e1e',
            }}
          />
        </Box>
      </Box>
    </Section>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────────────
function FinalCTA() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        backgroundColor: '#2C2C2C',
        borderTop: '2px solid #1e1e1e',
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
      }}
    >
      <Box sx={{ maxWidth: 700, mx: 'auto' }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'Montserrat, Inter, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#FFFFFF',
            mb: 2,
          }}
        >
          Start Your Classroom Newspaper Today
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Lato, Inter, sans-serif',
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: 'rgba(255,255,255,0.7)',
            lineHeight: 1.7,
            mb: 5,
          }}
        >
          Free to start. Set up in under a minute. Your first edition could be published this week.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <YellowButton onClick={() => navigate('/register')}>
            Get Started Free
          </YellowButton>
          <MuiButton
            variant="outlined"
            onClick={() => navigate('/contact')}
            sx={{
              borderColor: '#FFFFFF',
              border: '2px solid #FFFFFF',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '6px',
              boxShadow: '4px 4px 0px rgba(255,255,255,0.3)',
              px: 5,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: '#FFFFFF',
                boxShadow: '2px 2px 0px rgba(255,255,255,0.3)',
                transform: 'translate(2px, 2px)',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            Contact Sales
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function NewspaperLandingPage() {
  return (
    <LandingLayout>
      <Hero />
      <HowItWorks />
      <StoryTemplates />
      <SocialProof />
      <WhyNewspapers />
      <ForPrincipals />
      <PrivacyCompact />
      <FinalCTA />
    </LandingLayout>
  );
}
